import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, RotateCcw, Save, ShieldAlert } from "lucide-react";
import {
  useGetConfigurationSettings,
  useUpdateConfigurationSettings,
} from "@/resources/configuration-settings/configuration-settings.api";
import type { ConfigSetting } from "./types";
import { isValidConfigNumber } from "./utils";

type CategoryConfigSettingsPanelProps = {
  configSettings: ConfigSetting[];
};

export function CategoryConfigSettingsPanel({ configSettings }: CategoryConfigSettingsPanelProps) {
  const { data: configData, isLoading } = useGetConfigurationSettings();
  const updateMutation = useUpdateConfigurationSettings();

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(configSettings.map((s) => [s.alias, String(s.default)])),
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [initializedFor, setInitializedFor] = useState<string | null>(null);

  const aliasesKey = configSettings.map((s) => s.alias).join(",");
  useEffect(() => {
    if (!configData || initializedFor === aliasesKey) return;
    const map: Record<string, string> = {};
    for (const row of configData) map[row.alias] = row.value;
    setValues(
      Object.fromEntries(
        configSettings.map((s) => {
          const raw = map[s.alias];
          const v = raw !== undefined && !Number.isNaN(Number(raw)) ? String(Number(raw)) : String(s.default);
          return [s.alias, v];
        }),
      ),
    );
    setInitializedFor(aliasesKey);
    setHasChanges(false);
  }, [configData, initializedFor, aliasesKey, configSettings]);

  const isValid = configSettings.every((s) => isValidConfigNumber(values[s.alias] ?? ""));

  const handleSave = () => {
    updateMutation.mutate(
      { settings: configSettings.map((s) => ({ alias: s.alias, value: String(Number(values[s.alias])) })) },
      {
        onSuccess: () => {
          toast.success("Configuration saved");
          setHasChanges(false);
        },
      },
    );
  };

  const handleReset = () => {
    setValues(Object.fromEntries(configSettings.map((s) => [s.alias, String(s.default)])));
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border p-4 space-y-4 bg-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Configuration
        </h3>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-700 text-xs font-medium">
              <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
              Unsaved changes
            </span>
          )}
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" onClick={handleReset}>
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={handleSave}
            disabled={!hasChanges || !isValid || updateMutation.isPending}
          >
            <Save className="h-3.5 w-3.5" />
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {configSettings.map((s) => (
          <div key={s.alias} className="space-y-1">
            <Label htmlFor={`config-${s.alias}`} className="text-sm font-medium">
              {s.label}
            </Label>
            <p className="text-xs text-muted-foreground">{s.description}</p>
            <Input
              id={`config-${s.alias}`}
              type="number"
              min={0}
              step={1}
              value={values[s.alias] ?? ""}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, [s.alias]: e.target.value }));
                setHasChanges(true);
              }}
              className="w-32 h-9 tabular-nums"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
